import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X, ArrowRight } from "lucide-react";
import { nav, site } from "@/config/site";
import { services, serviceGroups } from "@/content/services";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

export const Logo = ({ className = "h-8" }) => (
  <img src={site.logo} alt="Rigorfield" width="187" height="57" className={`w-auto ${className}`} />
);

const ServicesMenu = () => (
  <div className="grid w-[880px] grid-cols-4 gap-8 p-8">
    {serviceGroups.map((g) => (
      <div key={g.id}>
        <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{g.label}</p>
        <ul className="space-y-2.5">
          {services.filter((s) => s.group === g.id).map((s) => (
            <li key={s.slug}><Link to={`/services/${s.slug}`} className="block text-sm font-medium leading-snug text-ink hover:text-teal">{s.title}</Link></li>
          ))}
        </ul>
      </div>
    ))}
    <div className="col-span-4 flex items-center justify-between border-t border-line pt-5">
      <p className="text-sm text-slate-600">Sixteen assessment disciplines. One independent standard.</p>
      <Link to="/services" className="inline-flex items-center gap-2 text-sm font-semibold text-teal">All services <ArrowRight className="h-4 w-4" /></Link>
    </div>
  </div>
);

const SubMenu = ({ items }) => (
  <ul className="w-[320px] p-3">
    {items.map((c) => (
      <li key={c.to}><Link to={c.to} className="block rounded-sm px-4 py-3 hover:bg-paper"><span className="block text-sm font-semibold text-ink">{c.label}</span><span className="block text-xs text-slate-500">{c.note}</span></Link></li>
    ))}
  </ul>
);

const DesktopItem = ({ item }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { pathname } = useLocation();
  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);
  const hasMenu = item.menu || item.children;
  const id = `menu-${item.label.toLowerCase()}`;
  const linkCls = ({ isActive }) => `relative px-3 py-2 text-[13.5px] font-semibold transition-colors ${isActive && item.to !== "/" ? "text-teal" : "text-ink hover:text-teal"}`;
  if (!hasMenu) return <NavLink to={item.to} end className={linkCls} data-testid={`nav-${item.label.toLowerCase()}`}>{item.label}</NavLink>;
  return (
    <div ref={ref} className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)} onBlur={(e) => { if (!ref.current?.contains(e.relatedTarget)) setOpen(false); }}>
      <button type="button" aria-expanded={open} aria-controls={id} onClick={(e) => setOpen((o) => (e.detail === 0 ? !o : true))} data-testid={`nav-${item.label.toLowerCase()}-trigger`} className="flex items-center gap-1 px-3 py-2 text-[13.5px] font-semibold text-ink hover:text-teal">
        {item.label}<ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden />
      </button>
      {open && <div id={id} className={item.menu ? "fixed inset-x-0 top-[60px] flex justify-center pt-3" : "absolute -left-4 top-full pt-3"}>
        <div className="rounded-md border border-line bg-white shadow-[0_24px_60px_-20px_rgba(10,22,40,.35)]">
          {item.menu ? <ServicesMenu /> : <SubMenu items={item.children} />}
        </div>
      </div>}
    </div>
  );
};

const MobileNav = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => { setOpen(false); }, [pathname]);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button type="button" className="inline-flex h-11 w-11 items-center justify-center rounded-sm border border-line lg:hidden" aria-label="Open menu" data-testid="mobile-menu-open">
          <Menu className="h-5 w-5" />
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full overflow-y-auto bg-white p-0 sm:max-w-md [&>button]:hidden" data-testid="mobile-menu">
        <SheetTitle className="sr-only">Site navigation</SheetTitle>
        <div className="flex h-16 items-center justify-between border-b border-line px-5">
          <Logo className="h-7" />
          <button type="button" onClick={() => setOpen(false)} className="inline-flex h-11 w-11 items-center justify-center" aria-label="Close menu" data-testid="mobile-menu-close"><X className="h-5 w-5" /></button>
        </div>
        <nav aria-label="Mobile" className="px-5 py-4">
          {nav.map((item) => (
            <div key={item.label} className="border-b border-line py-3">
              <Link to={item.to} className="block py-1 text-lg font-bold text-ink" data-testid={`mobile-nav-${item.label.toLowerCase()}`}>{item.label}</Link>
              {item.children && (
                <ul className="mt-2 space-y-1 pl-3">{item.children.map((c) => <li key={c.to}><Link to={c.to} className="block py-1.5 text-sm text-slate-600">{c.label}</Link></li>)}</ul>
              )}
            </div>
          ))}
          <Link to="/request-assessment" className="btn-primary mt-6 w-full py-4" data-testid="mobile-nav-cta">Request an Assessment</Link>
        </nav>
      </SheetContent>
    </Sheet>
  );
};

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 12);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  return (
    <header className={`fixed inset-x-0 top-0 z-50 border-b bg-white/[0.97] backdrop-blur-xl transition-[box-shadow,border-color] ${scrolled ? "border-line shadow-[0_8px_30px_-12px_rgba(10,22,40,.18)]" : "border-transparent"}`} data-testid="site-header">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-3 focus:z-50 focus:bg-ink focus:px-4 focus:py-2 focus:text-white">Skip to content</a>
      <div className="container-x flex h-16 items-center justify-between gap-6 lg:h-[76px]">
        <Link to="/" aria-label="Rigorfield home" data-testid="header-logo"><Logo className="h-7 lg:h-8" /></Link>
        <nav aria-label="Primary" className="hidden items-center lg:flex">
          {nav.map((item) => <DesktopItem key={item.label} item={item} />)}
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/request-assessment" className="btn-ink hidden py-2.5 sm:inline-flex" data-testid="header-cta">Request an Assessment</Link>
          <MobileNav />
        </div>
      </div>
    </header>
  );
};
